import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Tv, Check, X, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_META = {
  netflix:        { name: 'Netflix',      color: '#E50914' },
  hulu:           { name: 'Hulu',         color: '#1CE783' },
  disney_plus:    { name: 'Disney+',      color: '#113CCF' },
  hbo_max:        { name: 'Max',          color: '#B535F6' },
  amazon_prime:   { name: 'Prime Video',  color: '#00A8E1' },
  apple_tv:       { name: 'Apple TV+',    color: '#aaaaaa' },
  peacock:        { name: 'Peacock',      color: '#FDB927' },
  paramount_plus: { name: 'Paramount+',   color: '#0064FF' },
};

/**
 * App-Switch Inference confirm card (Part 5).
 *
 * Reached via the push notification the backend sends when a candidate
 * session (opened when the user switched to a streaming app directly, not
 * through Qude) is promoted after ~15 min of sustained playback. Nothing
 * from this flow ever reaches the hub without an explicit tap here — same
 * "no title = no write" contract as every other detection source.
 */
export default function AppSwitchConfirm() {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [guesses, setGuesses] = useState(null);
  const [service, setService] = useState('');
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(false);
  const [done, setDone] = useState(null); // 'confirmed' | 'dismissed'
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${API}/app-switch/candidate/${candidateId}/guesses`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setGuesses(res.data.guesses || []);
        setService(res.data.service || '');
      } catch (err) {
        console.error('Failed to load app-switch guesses:', err);
        setGuesses([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [candidateId, getToken]);

  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const token = await getToken();
        const res = await axios.get(`${API}/tmdb/search`, {
          params: { q: query },
          headers: { Authorization: `Bearer ${token}` },
        });
        setSearchResults(res.data.results || []);
      } catch (err) {
        console.error('Search failed:', err);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [query, getToken]);

  const confirm = async (guess) => {
    setResponding(true);
    try {
      const token = await getToken();
      await axios.post(`${API}/app-switch/candidate/${candidateId}/confirm`, {
        title: guess.title,
        tmdb_id: guess.tmdb_id || '',
        media_type: guess.media_type || 'tv',
      }, { headers: { Authorization: `Bearer ${token}` } });
      setDone('confirmed');
      setTimeout(() => navigate('/hub'), 1500);
    } catch (err) {
      console.error('Failed to confirm app-switch candidate:', err);
    } finally {
      setResponding(false);
    }
  };

  const dismiss = async () => {
    setResponding(true);
    try {
      const token = await getToken();
      await axios.post(`${API}/app-switch/candidate/${candidateId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDone('dismissed');
      setTimeout(() => navigate('/hub'), 1200);
    } catch (err) {
      console.error('Failed to dismiss app-switch candidate:', err);
    } finally {
      setResponding(false);
    }
  };

  const svc = SERVICE_META[service] || {};

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#050508' }}>
        <div style={{ width: 24, height: 24, border: '2px solid #7c3aed', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (done) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#050508', flexDirection: 'column', gap: 16, padding: 24 }}>
        <div style={{
          width: 72, height: 72, borderRadius: '50%',
          background: done === 'confirmed' ? 'linear-gradient(135deg, #22c55e, #4A9EFF)' : 'rgba(255,255,255,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {done === 'confirmed' ? <Check size={32} style={{ color: '#fff' }} /> : <X size={32} style={{ color: '#fff' }} />}
        </div>
        <p style={{ color: '#fff', fontSize: 18, fontWeight: 800, textAlign: 'center' }}>
          {done === 'confirmed' ? 'Added to your Hub!' : "Got it — we won't ask again this session."}
        </p>
        <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>Taking you back...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#050508', padding: 24 }}>
      <div style={{ maxWidth: 420, width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Tv size={28} style={{ color: '#4A9EFF' }} />
          </div>
          {svc.name && (
            <div style={{
              display: 'inline-block', fontSize: 11, fontWeight: 700, marginBottom: 10,
              color: svc.color, background: `${svc.color}20`,
              border: `1px solid ${svc.color}40`, borderRadius: 20, padding: '3px 12px',
            }}>
              {svc.name}
            </div>
          )}
          <h1 style={{ fontSize: 22, fontWeight: 900, color: '#fff', margin: '0 0 6px', lineHeight: 1.3 }}>
            Looks like you're watching {svc.name || 'something'} — what is it?
          </h1>
        </div>

        {/* Ranked guesses */}
        {guesses && guesses.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {guesses.map((g) => (
              <button
                key={g.title}
                onClick={() => confirm(g)}
                disabled={responding}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
                  padding: '10px 14px', borderRadius: 14,
                  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                  color: '#fff', cursor: 'pointer', opacity: responding ? 0.6 : 1,
                }}>
                {g.poster_url ? (
                  <img src={g.poster_url} alt="" style={{ width: 36, height: 52, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 36, height: 52, borderRadius: 6, background: '#1a1a1a', flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.title}</div>
                  {g.reason && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{g.reason}</div>}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Search field */}
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 12, color: 'rgba(255,255,255,0.3)' }} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Or search for the title..."
            style={{
              width: '100%', boxSizing: 'border-box', padding: '10px 12px 10px 36px', borderRadius: 14,
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
              color: '#fff', fontSize: 14, outline: 'none',
            }}
          />
          {searching && <div style={{ position: 'absolute', right: 12, top: 13, fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>...</div>}
        </div>

        {searchResults.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16, maxHeight: 240, overflowY: 'auto' }}>
            {searchResults.map((r) => (
              <button
                key={`${r.tmdb_id}-${r.title}`}
                onClick={() => confirm({ title: r.title, tmdb_id: String(r.tmdb_id), media_type: r.media_type, poster_url: r.poster_url })}
                disabled={responding}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
                  padding: '8px 12px', borderRadius: 12,
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                  color: '#fff', cursor: 'pointer',
                }}>
                {r.poster_url ? (
                  <img src={r.poster_url} alt="" style={{ width: 30, height: 44, borderRadius: 5, objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 30, height: 44, borderRadius: 5, background: '#1a1a1a', flexShrink: 0 }} />
                )}
                <div style={{ fontSize: 13, fontWeight: 600 }}>{r.title} {r.year ? <span style={{ color: 'rgba(255,255,255,0.35)' }}>({r.year})</span> : null}</div>
              </button>
            ))}
          </div>
        )}

        <button
          onClick={dismiss}
          disabled={responding}
          style={{
            width: '100%', padding: '12px', borderRadius: 14,
            background: 'transparent', border: '1px solid rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.5)', fontSize: 14, fontWeight: 700,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
          <X size={14} /> Not watching anything
        </button>
      </div>
    </div>
  );
}
